import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Heart, LayoutDashboard, Calendar, Settings, LogOut, CreditCard, ShieldCheck } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function AppShell({ children, chestId }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (user) {
      api.get("/admin/me").then((r) => setIsAdmin(r.data.is_admin)).catch(() => setIsAdmin(false));
    }
  }, [user]);

  const navItems = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, key: "dashboard" },
    chestId && { to: `/chest/${chestId}`, label: "Hope Chest", icon: Heart, key: "chest" },
    chestId && { to: `/planner/${chestId}`, label: "Hope Planner", icon: Calendar, key: "planner" },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#FCFAF8] text-[#3D3935] flex flex-col">
      <header className="border-b border-[#EBE5DF] bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="app-shell-logo">
            <img src="/hope-chest-logo.png" alt="Hope Chest" className="w-9 h-9 object-contain" />
            <span className="font-brand text-lg font-semibold">Hope Chest</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((it) => {
              const active = loc.pathname.startsWith(it.to);
              return (
                <Link
                  key={it.key}
                  to={it.to}
                  data-testid={`shell-nav-${it.key}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                    active ? "bg-[#D8E2D8] text-[#3A5A4A]" : "text-[#7A736E] hover:text-[#3D3935] hover:bg-[#F3EFEA]"
                  }`}
                >
                  <it.icon className="w-4 h-4" /> {it.label}
                </Link>
              );
            })}
          </nav>
          <DropdownMenu>
            <DropdownMenuTrigger className="outline-none" data-testid="shell-user-menu">
              <Avatar className="border border-[#EBE5DF]">
                <AvatarImage src={user?.picture || undefined} />
                <AvatarFallback className="bg-[#D8E2D8] text-[#3A5A4A] text-sm">
                  {user?.name?.[0]?.toUpperCase() || "?"}
                </AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-2xl">
              <DropdownMenuLabel>
                <div className="text-sm font-medium">{user?.name}</div>
                <div className="text-xs text-[#7A736E] font-normal">{user?.email}</div>
                <div className="mt-1.5 inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#D8E2D8] text-[#3A5A4A]">
                  {user?.subscription_tier === "paid" ? "Premium" : "Free"}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => nav("/settings")} data-testid="shell-menu-settings">
                <Settings className="w-4 h-4 mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => nav("/pricing")} data-testid="shell-menu-billing">
                <CreditCard className="w-4 h-4 mr-2" /> Membership
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={() => nav("/admin")} data-testid="shell-menu-admin">
                  <ShieldCheck className="w-4 h-4 mr-2" /> Admin / Checklist
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="shell-menu-logout">
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <main className="flex-1">{children}</main>
    </div>
  );
}
